import React, { useEffect, useState } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  Alert,
  CircularProgress
} from '@mui/material';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle as CheckCircleIcon } from '@mui/icons-material';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';

const SubscriptionReturn: React.FC = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);

  const updated = searchParams.get('updated');
  const canceled = searchParams.get('canceled');

  console.log('🔍 SubscriptionReturn Debug:', {
    hasUser: !!user,
    userId: user?.id,
    updated: !!updated,
    canceled: !!canceled,
    currentURL: window.location.href
  });

  useEffect(() => {
    // Give a moment for auth to load, then stop loading
    const timer = setTimeout(() => {
      setLoading(false);
    }, 2000);

    return () => clearTimeout(timer);
  }, []);

  const handleContinueToDashboard = () => {
    navigate('/dashboard');
  };

  const handleGoToSubscription = () => {
    navigate('/subscription');
  };

  const handleSignIn = () => {
    navigate('/login');
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
        }}
      >
        <Card sx={{ p: 4, textAlign: 'center' }}>
          <CircularProgress size={48} sx={{ mb: 2 }} />
          <Typography variant="h6">
            Processing your request...
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Please wait a moment.
          </Typography>
        </Card>
      </Box>
    );
  }

  // User is logged in
  if (user) {
    return (
      <Box
        sx={{
          minHeight: '100vh',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          py: 4
        }}
      >
        <Container maxWidth="md">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <Card sx={{ p: 6, textAlign: 'center' }}>
              <CheckCircleIcon
                sx={{
                  fontSize: 80,
                  color: 'success.main',
                  mb: 2
                }}
              />

              <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
                {updated ? 'Subscription Updated!' : canceled ? 'Subscription Canceled' : 'All Set!'} 
              </Typography>

              <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
                {updated && 'Your subscription changes have been processed successfully.'}
                {canceled && 'Your subscription has been canceled as requested.'}
                {!updated && !canceled && 'Your subscription management session is complete.'}
              </Typography>

              {updated && (
                <Alert severity="success" sx={{ mb: 4, textAlign: 'left' }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Changes Applied</strong>
                  </Typography>
                  <Typography variant="body2">
                    Any subscription changes you made have been processed. You should receive a confirmation email shortly.
                  </Typography>
                </Alert>
              )}

              {canceled && (
                <Alert severity="info" sx={{ mb: 4, textAlign: 'left' }}>
                  <Typography variant="body1" gutterBottom>
                    <strong>Subscription Canceled</strong>
                  </Typography>
                  <Typography variant="body2">
                    Your subscription has been canceled. You'll continue to have access to premium features until the end of your current billing period.
                  </Typography>
                </Alert>
              )}

              <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                <Button
                  variant="contained"
                  size="large"
                  onClick={handleContinueToDashboard}
                  sx={{
                    background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
                    }
                  }}
                >
                  Continue to Dashboard
                </Button>

                <Button
                  variant="outlined"
                  size="large"
                  onClick={handleGoToSubscription}
                >
                  View Subscription
                </Button>
              </Box>
            </Card>
          </motion.div>
        </Container>
      </Box>
    );
  }

  // User not logged in
  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <Card sx={{ p: 6, textAlign: 'center' }}>
            <CheckCircleIcon
              sx={{
                fontSize: 80,
                color: 'success.main',
                mb: 2
              }}
            />

            <Typography variant="h3" gutterBottom sx={{ fontWeight: 'bold' }}>
              Subscription Changes Complete
            </Typography>

            <Typography variant="h6" color="text.secondary" sx={{ mb: 4 }}>
              Your subscription management session is complete
            </Typography>

            <Alert severity="info" sx={{ mb: 4, textAlign: 'left' }}>
              <Typography variant="body1" gutterBottom>
                <strong>Sign in to view your subscription</strong>
              </Typography>
              <Typography variant="body2">
                Please sign in to your account to view your updated subscription details and access your premium features.
              </Typography>
            </Alert>

            <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
              <Button
                variant="contained"
                size="large"
                onClick={handleSignIn}
                sx={{
                  background: 'linear-gradient(45deg, #667eea 30%, #764ba2 90%)',
                  '&:hover': {
                    background: 'linear-gradient(45deg, #5a67d8 30%, #6b46c1 90%)',
                  }
                }}
              >
                Sign In
              </Button>

              <Button
                variant="outlined"
                size="large"
                onClick={() => navigate('/pricing')}
              >
                View Plans
              </Button>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
              You'll receive a confirmation email with details about any changes made.
            </Typography>
          </Card>
        </motion.div>
      </Container>
    </Box>
  );
};

export default SubscriptionReturn; 